import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Header, Footer, PageCard } from './index';
import { CaseListItem, getStatusConfig } from '../types';
import { getCases } from '../hooks/cases';
import { defaultBrowserLocalRecordingRecovery } from '../utils/browserLocalRecordingRecovery';
import { formatRecordingDuration } from '../utils/formatRecordingDuration';
import { apiFetch } from '../utils/api';
import { toast } from 'sonner';
import projectConfigJson from '../types/project_config_131.json';

interface NewCase {
  id: string;
  name: string;
}

const TEST_USER_ID = 10;

const getArchiveReason = (caseItem: CaseListItem): CaseListItem['archive_reason'] => caseItem.archive_reason ?? null;

const isArchivedCase = (caseItem: CaseListItem): boolean => Boolean(getArchiveReason(caseItem));

const getArchiveStatusLabel = (caseItem: CaseListItem): string => {
  const reason = getArchiveReason(caseItem);
  if (reason === 'cleaned') return 'Opgeschoond';
  if (reason === 'expired') return 'Verlopen';
  return getStatusConfig(caseItem.status || 'unknown', caseItem).displayName;
};

const formatCaseDate = (value?: string | null): string => {
  if (!value) return 'Onbekend';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Onbekend';
  return date.toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const headingClassName = 'text-2xl lg:text-3xl font-semibold text-brand-red';
const headerCellClassName = 'whitespace-nowrap px-5 py-4 font-semibold text-base';
const rowClassName = 'text-brand-blauw bg-[#FBF2E5] hover:bg-brand-primary/20 cursor-pointer';
const cellClassName = 'px-5 py-4 text-base';
const emptyCellClassName = 'px-5 py-4 text-base text-brand-dark-gray';
const nameLinkClassName =
  'text-inherit hover:underline focus:outline-none focus:ring-2 focus:ring-brand-blauw rounded';

// Shared by the two active tables so their columns line up.
// Deliberately worded as device-local: this recording exists only in this
// browser, so it must not read as a status the whole team can see.
const PendingRecordingBadge: React.FC<{ durationMs?: number }> = ({ durationMs }) => {
  const duration = formatRecordingDuration(durationMs);

  return (
    <div
      className="flex flex-col items-start gap-1"
      title="Er staat nog een niet-verzonden opname in deze browser. Open het gesprek om verder te gaan of af te ronden."
    >
      <span className="status-badge bg-[#F09888] text-brand-beige">
        Opname gepauzeerd
      </span>
      {/* Beneath the pill rather than inside it: the status column is a fixed
          20% of the table and clipped the duration off the end. */}
      {duration && (
        <span className="text-xs font-medium text-brand-dark-gray">
          {duration} audio
        </span>
      )}
    </div>
  );
};

const ActiveTableHead: React.FC = () => (
  <>
    <colgroup>
      <col className="col-name" />
      <col className="col-gender" />
      <col className="col-dossier" />
      <col className="col-date" />
      <col className="col-status" />
    </colgroup>
    <thead>
      <tr className="text-left text-brand-blauw bg-[#F2E9DA]">
        <th scope="col" className={headerCellClassName}>Naam</th>
        <th scope="col" className={headerCellClassName}>Geslacht</th>
        <th scope="col" className={headerCellClassName}>Dossier nummer</th>
        <th scope="col" className={headerCellClassName}>Aangemaakt</th>
        <th scope="col" className={headerCellClassName}>Status</th>
      </tr>
    </thead>
  </>
);

const ListCasesPage: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<CaseListItem[]>([]);
  // Conversation ids with an unfinished recording still sitting in this
  // browser: either paused, or interrupted by a closed tab. The backend knows
  // nothing about these, so the status column alone would show "Open".
  const [pendingRecordings, setPendingRecordings] = useState<Map<string, number | undefined>>(new Map());
  const [isCreatingTestConversation, setIsCreatingTestConversation] = useState(false);
  const [showArchivedCases, setShowArchivedCases] = useState(false);
  const isTestMode = process.env.REACT_APP_USE_TEST_JWT === 'true';

  const handleCreateTestConversation = async () => {
    if (!isTestMode || isCreatingTestConversation) return;

    setIsCreatingTestConversation(true);
    const now = new Date();
    const name = `Testgesprek ${now.toLocaleString('nl-NL')}`;
    const projectQuestionnaireId = (projectConfigJson as any).projectQuestionnaires?.[0]?.id ?? 373;

    try {
      const response = await apiFetch('/conversation/register', {
        method: 'POST',
        body: JSON.stringify({
          conversation_compass_session: {
            id: Math.floor(Date.now() / 1000),
            user_id: TEST_USER_ID,
            project_questionnaire_id: projectQuestionnaireId,
            candidate_id: 456,
            name,
          },
          project_config: projectConfigJson,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create test conversation: ${response.status} ${response.statusText}`);
      }

      const newCase: NewCase = await response.json();
      toast.success('Testgesprek aangemaakt');
      navigate(`/${newCase.id}/record`, { state: { caseName: newCase.name } });
    } catch (error) {
      console.error('Error creating test conversation:', error);
      toast.error('Testgesprek kon niet worden aangemaakt');
    } finally {
      setIsCreatingTestConversation(false);
    }
  };

  useEffect(() => {
    getCases().then(cases => setCases(cases));
  }, []);

  useEffect(() => {
    let cancelled = false;

    defaultBrowserLocalRecordingRecovery
      .listPendingRecordings()
      .then((pending) => {
        if (cancelled) return;
        setPendingRecordings(
          new Map(pending.map((entry) => [entry.conversationId, entry.durationMs]))
        );
      })
      .catch((error) => {
        // Recovery storage being unavailable must not break the case list.
        console.error('Could not read pending browser-local recordings:', error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const newCases = cases.filter(caseItem => caseItem.status === 'open' && !isArchivedCase(caseItem));
  const ongoingCases = cases.filter(caseItem => caseItem.status !== 'open' && !isArchivedCase(caseItem));
  const archivedCases = cases.filter(isArchivedCase);

  return (
    <div className="min-h-screen bg-brand-primary flex flex-col">

      <Header />

      <PageCard>
        <div className="space-y-10">
          {/* Nieuwe gesprekken */}
          <section className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <h1 className={headingClassName}>Nieuwe gesprekken</h1>
              {isTestMode && (
                <button
                  type="button"
                  onClick={handleCreateTestConversation}
                  disabled={isCreatingTestConversation}
                  className="px-4 py-2 rounded-lg bg-brand-blauw text-brand-beige font-medium transition-colors hover:bg-brand-blauw/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingTestConversation ? 'Testgesprek aanmaken…' : 'Nieuw testgesprek'}
                </button>
              )}
            </div>

            <div className="-mx-4 sm:mx-0 overflow-x-auto pt-2">
              <table className="table-conversations min-w-[768px]">
                <ActiveTableHead />
                <tbody>
                  {newCases.length === 0 ? (
                    <tr className="text-brand-blauw bg-[#FBF2E5]">
                      <td colSpan={5} className={emptyCellClassName}>
                        Er zijn op dit moment geen nieuwe gesprekken. Ga naar{' '}
                        <a
                          href="https://sys.competensys.nl"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-blauw hover:underline"
                        >
                          CompetenSYS
                        </a>{' '}
                        om een nieuw gesprek aan te maken.
                      </td>
                    </tr>
                  ) : newCases.map((caseItem) => (
                    <tr
                      key={caseItem.id}
                      onClick={() => navigate(`/${caseItem.id}/record`, { state: { caseName: caseItem.name } })}
                      className={rowClassName}
                    >
                      <td className={`${cellClassName} font-medium`}>
                        <Link
                          to={`/${caseItem.id}/record`}
                          state={{ caseName: caseItem.name }}
                          className={nameLinkClassName}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {caseItem.name}
                        </Link>
                      </td>
                      <td className={cellClassName}>&mdash;</td>
                      <td className={cellClassName}>&mdash;</td>
                      <td className={cellClassName}>{formatCaseDate(caseItem.created_at)}</td>
                      <td className={cellClassName}>
                        {(() => {
                          // A pending local recording is the more specific
                          // truth: showing it beside "Open" tells two stories.
                          if (pendingRecordings.has(caseItem.id)) {
                            return <PendingRecordingBadge durationMs={pendingRecordings.get(caseItem.id)} />;
                          }

                          const statusConfig = getStatusConfig(caseItem.status || 'unknown', caseItem);
                          return (
                            <span className={`status-badge ${statusConfig.color}`}>
                              {statusConfig.displayName}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Lopende gesprekken */}
          <section className="space-y-4">
            <h1 className={headingClassName}>Lopende gesprekken</h1>

            <div className="-mx-4 sm:mx-0 overflow-x-auto pt-2">
              <table className="table-conversations min-w-[768px]">
                <ActiveTableHead />
                <tbody>
                  {ongoingCases.length === 0 ? (
                    <tr className="text-brand-blauw bg-[#FBF2E5]">
                      <td colSpan={5} className={emptyCellClassName}>
                        Er zijn op dit moment geen lopende gesprekken.
                      </td>
                    </tr>
                  ) : ongoingCases.map((caseItem) => {
                    const statusConfig = getStatusConfig(caseItem.status || 'open', caseItem);
                    return (
                      <tr
                        key={caseItem.id}
                        onClick={() => navigate(statusConfig.uri || '/', { state: { caseName: caseItem.name } })}
                        className={rowClassName}
                      >
                        <td className={`${cellClassName} font-medium`}>
                          <Link
                            to={statusConfig.uri || '/'}
                            state={{ caseName: caseItem.name }}
                            className={nameLinkClassName}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {caseItem.name}
                          </Link>
                        </td>
                        <td className={cellClassName}>&mdash;</td>
                        <td className={cellClassName}>&mdash;</td>
                        <td className={cellClassName}>{formatCaseDate(caseItem.created_at)}</td>
                        <td className={cellClassName}>
                          {pendingRecordings.has(caseItem.id) ? (
                            <PendingRecordingBadge durationMs={pendingRecordings.get(caseItem.id)} />
                          ) : (
                            <span className={`status-badge ${statusConfig.color}`}>
                              {statusConfig.displayName}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Gearchiveerde gesprekken */}
          <section className="space-y-4">
            <h1 className={headingClassName}>Gearchiveerde gesprekken</h1>

            <div className="rounded-lg overflow-hidden bg-[#FBF2E5]">
              <button
                type="button"
                onClick={() => setShowArchivedCases(open => !open)}
                aria-expanded={showArchivedCases}
                className="w-full px-5 py-4 flex items-center justify-between text-left text-base font-medium text-brand-blauw hover:bg-brand-primary/20 transition-colors"
              >
                <span>{archivedCases.length} gearchiveerd of verzonden</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showArchivedCases ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              {showArchivedCases && (
                <div className="overflow-x-auto">
                  <table className="table-conversations min-w-[768px]">
                    <thead>
                      <tr className="text-left text-brand-blauw bg-[#F2E9DA]">
                        <th scope="col" className={headerCellClassName}>Naam</th>
                        <th scope="col" className={headerCellClassName}>Aangemaakt</th>
                        <th scope="col" className={headerCellClassName}>Verstuurd</th>
                        <th scope="col" className={headerCellClassName}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedCases.length === 0 && (
                        <tr className="text-brand-blauw bg-[#FBF2E5]">
                          <td colSpan={4} className={emptyCellClassName}>
                            Er zijn nog geen gearchiveerde gesprekken.
                          </td>
                        </tr>
                      )}
                      {archivedCases.map((caseItem) => {
                        const statusConfig = getStatusConfig(caseItem.status || 'closed', caseItem);
                        const archiveReason = getArchiveReason(caseItem);
                        const canInspectReadOnly = caseItem.can_inspect === true && archiveReason === 'closed';
                        return (
                          <tr key={caseItem.id} className="text-brand-blauw bg-[#FBF2E5]">
                            <td className={`${cellClassName} font-medium`}>
                              {canInspectReadOnly ? (
                                <Link
                                  to={statusConfig.uri || '/'}
                                  state={{ caseName: caseItem.name }}
                                  className={nameLinkClassName}
                                >
                                  {caseItem.name}
                                </Link>
                              ) : (
                                <span>{caseItem.name}</span>
                              )}
                            </td>
                            <td className={cellClassName}>{formatCaseDate(caseItem.created_at)}</td>
                            <td className={cellClassName}>
                              {caseItem.submitted_at ? formatCaseDate(caseItem.submitted_at) : '—'}
                            </td>
                            <td className={cellClassName}>
                              <span className={`status-badge ${statusConfig.color}`}>
                                {getArchiveStatusLabel(caseItem)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </PageCard>

      <Footer />

    </div>
  );
};

export default ListCasesPage;
